/**
 * @file HostElementsContext.tsx
 * @description Provides a context and custom hook for managing the visibility of host elements
 * in the WordPress site that contains the React application. This allows React components
 * to control the display of elements outside the React root.
 */

import React, {
  createContext,
  useContext,
  useRef,
  useCallback,
  ReactNode,
  useEffect,
} from "react";
import { useLocation, matchPath } from "react-router-dom";
import { routeElementsConfig } from "../config/routeElementsConfig";
import { isColorLight } from "../components/utilities/colorUtils";

interface HostElementsContextType {
  toggleHostElements: (selectors: string[], visible: boolean) => void;
  setSidelineLabel: (label: string) => void;
  setPageBackground: (background: string | null) => void;
}

const HostElementsContext = createContext<HostElementsContextType | undefined>(
  undefined
);

interface HostElementsProviderProps {
  children: ReactNode;
}

export const HostElementsProvider: React.FC<HostElementsProviderProps> = ({
  children,
}) => {
  const location = useLocation();
  const hiddenElementsRef = useRef<Set<string>>(new Set());
  const sidelineLabelRef = useRef<string>("");

  // Toggle visibility of host elements
  const toggleHostElements = useCallback(
    (selectors: string[], visible: boolean) => {
      console.log("toggleHostElements called", selectors, visible);
      selectors.forEach((selector) => {
        const elements = document.querySelectorAll(selector);
        elements.forEach((element) => {
          if (element instanceof HTMLElement) {
            element.style.display = visible ? "" : "none";
          }
        });
      });

      if (visible) {
        selectors.forEach((selector) =>
          hiddenElementsRef.current.delete(selector)
        );
      } else {
        selectors.forEach((selector) =>
          hiddenElementsRef.current.add(selector)
        );
      }
    },
    []
  );

  // Set the sideline label and update related CSS variable
  const setSidelineLabel = useCallback((label: string) => {
    console.log("setSidelineLabel called", label);
    sidelineLabelRef.current = label;
    const sidelineLabelElement = document.querySelector(
      "#section-ai-seo-writer-tool > .section-label"
    );
    const sidelineElement = document.querySelector(
      "#section-ai-seo-writer-tool > .side-line"
    );

    if (sidelineLabelElement instanceof HTMLElement) {
      sidelineLabelElement.textContent = label;
    }

    if (sidelineElement instanceof HTMLElement) {
      const labelLength = label.length;
      sidelineElement.style.setProperty("--fps-sll", `${labelLength}ch`);
    }
  }, []);

  // Set the page background
  const setPageBackground = useCallback((background: string | null) => {
    console.log("setPageBackground called", background);
    const pageElement = document.querySelector("#main>article");
    const sectionElement = document.querySelector(
      "#section-ai-seo-writer-tool"
    );

    if (pageElement instanceof HTMLElement) {
      if (background) {
        pageElement.style.background = background;

        if (sectionElement instanceof HTMLElement) {
          if (isColorLight(background)) {
            sectionElement.classList.remove("reverse");
          } else {
            sectionElement.classList.add("reverse");
          }
        }
      } else {
        pageElement.style.removeProperty("background");

        if (sectionElement instanceof HTMLElement) {
          sectionElement.classList.add("reverse");
        }
      }
    }
  }, []);

  // Function to find the matching route configuration
  const findMatchingRouteConfig = (currentPath: string) => {
    for (const [route, config] of Object.entries(routeElementsConfig)) {
      if (matchPath(route, currentPath)) {
        return config;
      }
    }
    return null;
  };

  // Effect to handle route changes and apply configurations
  useEffect(() => {
    console.log("Route changed effect", location.pathname);
    const currentPath = location.pathname;
    const config = findMatchingRouteConfig(currentPath);
    const currentHiddenElements = hiddenElementsRef.current;

    if (config) {
      toggleHostElements(config.hide, false);
      toggleHostElements(config.show, true);
      if (config.sidelineLabel) {
        setSidelineLabel(config.sidelineLabel);
      }
      setPageBackground(config.pageBackground || null);
    } else {
      // If there's no config for this route, reset to default
      setPageBackground(null);
    }

    // Cleanup function to restore visibility of hidden elements
    return () => {
      currentHiddenElements.forEach((selector) => {
        const elements = document.querySelectorAll(selector);
        elements.forEach((element) => {
          if (element instanceof HTMLElement) {
            element.style.display = "";
          }
        });
      });
      currentHiddenElements.clear();
    };
  }, [location, toggleHostElements, setSidelineLabel, setPageBackground]);

  return (
    <HostElementsContext.Provider
      value={{ toggleHostElements, setSidelineLabel, setPageBackground }}
    >
      {children}
    </HostElementsContext.Provider>
  );
};

/**
 * Custom hook to use the HostElements context
 * @throws {Error} If used outside of a HostElementsProvider
 */
export const useHostElements = (): HostElementsContextType => {
  const context = useContext(HostElementsContext);
  if (context === undefined) {
    throw new Error(
      "useHostElements must be used within a HostElementsProvider"
    );
  }
  return context;
};
